import type {
	IPressReleaseEntry,
	INewsletterEntry,
	INewsItemEntry,
	IAsset,
} from '../../../../../contentful';
import { IDropdownItem } from '../../../../../ui';
import { sortCategoryLabelsAlphabetically } from './sortCategoryLabelsAlphabetically';

type IGenericNewsItem = INewsItemEntry | INewsletterEntry | IPressReleaseEntry;

export function getMediaCreatedAtYear(item: IAsset) {
	const createdAtDate = item.createdAt;
	return new Date(createdAtDate).getFullYear().toString();
}

export function getPublicationYear(
	item: INewsItemEntry | INewsletterEntry | IPressReleaseEntry,
) {
	const publicationDate = item.fields.publicationDate || item.createdAt || null;
	if (!publicationDate) {
		return undefined;
	}
	return new Date(publicationDate).getFullYear().toString();
}

export function getMediaSelectableYears(assets: IAsset[]) {
	return Array.from(
		new Set(assets.map(getMediaCreatedAtYear).filter((i) => i !== null)),
	) as string[];
}

export function getSelectableYears(
	mostRecent: (INewsItemEntry | INewsletterEntry | IPressReleaseEntry)[],
) {
	return Array.from(
		new Set(mostRecent.map(getPublicationYear).filter((i) => i !== null)),
	) as string[];
}

const getIsQueryInContent = (
	item: IGenericNewsItem | IAsset,
	queryText: string,
) => {
	const fields = item.fields as Record<string, unknown>;

	const lower = (v: unknown) => (typeof v === 'string' ? v.toLowerCase() : '');
	const q = (queryText ?? '').toLowerCase();

	const title = lower(fields.title);
	const body = lower(fields.body);
	const summary = lower(fields.summary);

	return body.includes(q) || summary.includes(q) || title.includes(q);
};

export function makeFilter(
	queryText: string,
	selectedYear: string,
	yearReset: string,
	selectedCategories?: IDropdownItem[],
) {
	return (item: IGenericNewsItem | IAsset) => {
		const isAsset = 'type' in item;
		const isNewsArticle =
			'contentType' in item && item.contentType === 'newsArticle';

		const isQueryInContent = getIsQueryInContent(item, queryText);

		const isPublishedInSelectedYear =
			selectedYear === yearReset ||
			(isAsset
				? getMediaCreatedAtYear(item as IAsset)
				: getPublicationYear(item as IGenericNewsItem)) === selectedYear;

		// handle category filtering only for news articles

		let itemHasCategory = true;
		const hasSelectedCategories =
			selectedCategories && selectedCategories?.length > 0;

		if (isNewsArticle && hasSelectedCategories) {
			const { categories } = (item as INewsItemEntry).fields;
			itemHasCategory = categories?.some((category) =>
				selectedCategories.includes(category.fields.categoryLabel),
			);
		}

		return isNewsArticle
			? isQueryInContent && isPublishedInSelectedYear && itemHasCategory
			: isQueryInContent && isPublishedInSelectedYear;
	};
}

export function normalizePublicationDate<
	T extends INewsItemEntry | INewsletterEntry,
>(item: T): T {
	return {
		...item,
		fields: { ...item.fields, publicationDate: getPublicationYear(item) },
	};
}

export function getAllCategoryLabels(allItems?: INewsItemEntry[]) {
	if (!allItems) return [];

	const categoryItems = allItems
		.flatMap((item) => item.fields.categories)
		.filter((category) => category !== undefined);

	// Split items based on priority
	const prioritisedCategories = categoryItems
		.filter((item) => item.fields.priority)
		.flatMap((item) => item.fields.categoryLabel);

	const nonPrioritisedCategories = categoryItems
		.filter((item) => !item.fields.priority)
		.flatMap((item) => item.fields.categoryLabel);

	// Remove duplicates
	const uniquePrioritisedLabels = Array.from(new Set(prioritisedCategories));
	const uniqueNonPrioritisedLabels = Array.from(
		new Set(nonPrioritisedCategories),
	);

	const sortedPrioritisedLabels = sortCategoryLabelsAlphabetically(
		uniquePrioritisedLabels,
	);
	const sortedNonPrioritisedLabels = sortCategoryLabelsAlphabetically(
		uniqueNonPrioritisedLabels,
	);

	// Categories with priority toggle need to be at the top of the list
	return [...sortedPrioritisedLabels, ...sortedNonPrioritisedLabels];
}
